import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { handleErrors } from "../../services/dataManager";
import {
  addNewAchievement,
  updateAchievement,
  uploadAchievementIcon,
} from "../../services/axios/apiManager";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { baseURL } from "../../services/axios/eps";
import { Formik } from "formik";
import * as Yup from "yup";
import LoadingSpinner from "../../Components/spinner";

const AddEditAchievementModal = ({
  selectedAchievement,
  setSelectedAchievement,
  openAddEditModal,
  setOpenAddEditModal,
  fetchAchievements,
  pagination,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageURL, setImageURL] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);

  const AchievementValidations = Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, "Achievement name must be at least 2 characters")
      .max(20, "Achievement name cannot exceed 20 characters")
      .required("Achievement name is required"),
    description: Yup.string().trim().required("Description is required."),
    icon: Yup.mixed().required("Icon is required"),
  });

  const handleCloseAddEditModal = () => {
    setSelectedAchievement({});
    setOpenAddEditModal(false);
    setImageURL(null);
    setImageChanged(false);
  };

  const uploadIcon = async (id, icon) => {
    if (imageChanged) {
      const formData = new FormData();
      formData.append("image", icon);
      const res = await uploadAchievementIcon(id, formData);
      if (res?.data.success) {
        toast.success("Achievement Icon has been uploaded", { duration: 2000 });
      } else {
        handleErrors(res, navigate);
      }
    }
  };

  const handleSubmitForm = async (data) => {
    setLoading(true);
    if (selectedAchievement?.id) {
      // // // Edit Achievement
      const { icon, ...values } = data;
      const res = await updateAchievement({
        id: selectedAchievement.id,
        ...values,
      });
      if (res?.data?.success) {
        toast.success("Achievement has been updated.", { duration: 2000 });
        await uploadIcon(selectedAchievement.id, icon);
        await fetchAchievements(pagination);
        handleCloseAddEditModal();
      } else {
        handleErrors(res, navigate, "Update Achievement:");
      }
    } else {
      // // // Add Achievement
      const { icon, ...values } = data;
      const res = await addNewAchievement({ ...values });
      if (res?.data?.success) {
        const id = res?.data?.data?.id;
        if (id) {
          await uploadIcon(id, icon);
        }
        await fetchAchievements(pagination);
        toast.success("A new Achievement has been added.", { duration: 2000 });
        if (id) handleCloseAddEditModal();
      } else {
        handleErrors(res, navigate, "Add Achievement:");
      }
    }
    setLoading(false);
  };

  const makeIconURL = (file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImageURL(previewUrl);
      setImageChanged(true);
    }
  };

  return (
    <Modal
      show={openAddEditModal}
      onHide={handleCloseAddEditModal}
      centered
      className="text-light"
    >
      <Modal.Header closeButton className="bg-dark text-light border-0">
        <Modal.Title>
          {selectedAchievement?.id ? "Edit achievement" : "Add achievement"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        <Formik
          initialValues={{
            name: selectedAchievement?.name || "",
            description: selectedAchievement?.description || "",
            requirements: selectedAchievement?.requirements || "",
            icon: selectedAchievement?.icon || null,
          }}
          validationSchema={AchievementValidations}
          onSubmit={async (values) => {
            try {
              setLoading(true);
              await handleSubmitForm(values);
            } catch (error) {
              console.log("Error upon Add Update Achievement", error);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({
            handleChange,
            setFieldValue,
            handleSubmit,
            values,
            errors,
            touched,
          }) => (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Achievement Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter achievement name"
                  name="name"
                  value={values.name}
                  onChange={handleChange("name")}
                  className="form-control bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.name && touched?.name ? (
                  <span className="text-danger">{errors?.name}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter description"
                  name="description"
                  value={values.description}
                  onChange={handleChange("description")}
                  className="form-control bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.description && touched?.description ? (
                  <span className="text-danger">{errors?.description}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Requirements</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Requirements"
                  name="requirements"
                  value={values.requirements}
                  onChange={handleChange("requirements")}
                  className="form-control bg-dark text-light border-light input-placeholder-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Achievement Icon</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  className="bg-dark text-light border-light"
                  data-testid="achievement-icon-input"
                  onChange={(event) => {
                    if (event?.currentTarget?.files?.length) {
                      setFieldValue("icon", event.currentTarget.files[0]);
                      makeIconURL(event.currentTarget.files[0]);
                    } else {
                      setFieldValue("icon", selectedAchievement.icon);
                      setImageURL(null);
                    }
                  }}
                />
                {imageURL ||
                  (selectedAchievement?.icon && (
                    <img
                      crossorigin="anonymous"
                      src={
                        imageURL
                          ? imageURL
                          : selectedAchievement?.icon
                            ? `${baseURL}/uploads/file/${selectedAchievement.icon}`
                            : null
                      }
                      style={{
                        maxWidth: "20%",
                        marginTop: "10px",
                        borderRadius: "5px",
                      }}
                    />
                  ))}
                {errors?.icon && touched?.icon ? (
                  <span className="text-danger">{errors?.icon}</span>
                ) : null}
              </Form.Group>
              <div className="constainer-fluid">
                <div className="d-flex justify-content-end">
                  {loading && (
                    <div className="mt-4 mx-2">
                      <LoadingSpinner size={"lg"} />
                    </div>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      handleCloseAddEditModal();
                    }}
                    className="mt-3 mx-2"
                  >
                    Close
                  </Button>
                  <Button
                    variant="success"
                    className="mt-3"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    {selectedAchievement?.id ? "Update" : "Add New"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditAchievementModal;
